import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { clsx } from 'clsx';
import {
  ArrowLeft,
  Monitor,
  Smartphone,
  Globe,
  Clock,
  LogOut,
  Menu,
  Shield,
} from 'lucide-react';
import { useChatLayout } from '@/components/layout/ChatLayout';
import { teamApi } from '@/services/team-api';
import { ConfirmationModal } from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import type { UserSession } from '@/types';

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function deviceIcon(label: string): React.ReactNode {
  const lower = label.toLowerCase();
  if (lower.includes('mobile') || lower.includes('iphone') || lower.includes('android')) {
    return <Smartphone className="w-5 h-5" />;
  }
  return <Monitor className="w-5 h-5" />;
}

const SessionsPage: React.FC = () => {
  const { isMobile, toggleSidebar } = useChatLayout();
  const queryClient = useQueryClient();
  const [confirmRevokeAll, setConfirmRevokeAll] = React.useState(false);

  const sessionsQuery = useQuery({
    queryKey: ['mySessions'],
    queryFn: teamApi.listSessions,
  });

  const revokeMutation = useMutation({
    mutationFn: (sessionId: string) => teamApi.revokeSession(sessionId),
    onSuccess: () => {
      toast.success('Session signed out');
      queryClient.invalidateQueries({ queryKey: ['mySessions'] });
    },
  });

  const revokeAllMutation = useMutation({
    mutationFn: () => teamApi.revokeAllOtherSessions(),
    onSuccess: () => {
      toast.success('All other sessions signed out');
      queryClient.invalidateQueries({ queryKey: ['mySessions'] });
      setConfirmRevokeAll(false);
    },
  });

  const sessions = sessionsQuery.data?.sessions ?? [];
  const otherSessions = sessions.filter(s => !s.is_current);

  return (
    <div className="flex flex-col min-h-screen px-4 py-8 sm:py-12 relative">
      {isMobile && (
        <button onClick={toggleSidebar} className="absolute top-4 left-4 p-2 rounded-lg text-muted-foreground hover:bg-accent transition-colors" aria-label="Open sidebar">
          <Menu className="w-5 h-5" />
        </button>
      )}

      <div className="w-full max-w-2xl mx-auto space-y-6">
        <Link to="/settings" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Settings
        </Link>

        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Active Sessions</h1>
            <p className="text-muted-foreground mt-1">Manage your active login sessions</p>
          </div>
          {otherSessions.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              leftIcon={<LogOut className="w-4 h-4" />}
              onClick={() => setConfirmRevokeAll(true)}
            >
              Sign Out All Others
            </Button>
          )}
        </div>

        {sessionsQuery.isLoading ? (
          <div className="flex justify-center py-12"><LoadingSpinner size="lg" /></div>
        ) : (
          <div className="rounded-xl bg-muted border border-border divide-y divide-border overflow-hidden">
            {sessions.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">No active sessions found.</div>
            ) : (
              sessions.map((session) => (
                <SessionRow
                  key={session.id}
                  session={session}
                  isRevoking={revokeMutation.isPending}
                  onRevoke={() => revokeMutation.mutate(session.id)}
                />
              ))
            )}
          </div>
        )}

        <div className="p-4 rounded-lg bg-info/15 border border-info/20">
          <div className="flex gap-2">
            <Shield className="w-5 h-5 text-info flex-shrink-0 mt-0.5" />
            <p className="text-sm text-info">
              If you see a session you don't recognize, sign it out immediately and change your password.
            </p>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={confirmRevokeAll}
        onClose={() => setConfirmRevokeAll(false)}
        onConfirm={() => revokeAllMutation.mutate()}
        title="Sign Out All Other Sessions"
        message="This will sign out all sessions except your current one. You'll stay logged in on this device."
        confirmText="Sign Out All"
        variant="destructive"
        isLoading={revokeAllMutation.isPending}
      />
    </div>
  );
};

interface SessionRowProps {
  session: UserSession;
  isRevoking: boolean;
  onRevoke: () => void;
}

const SessionRow: React.FC<SessionRowProps> = ({ session, isRevoking, onRevoke }) => (
  <div className="flex items-center justify-between p-4 sm:p-5">
    <div className="flex items-center gap-3 min-w-0">
      <div className={clsx(
        'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0',
        session.is_current
          ? 'bg-success/15 text-success'
          : 'bg-muted text-muted-foreground'
      )}>
        {deviceIcon(session.device_label || '')}
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-foreground truncate">
            {session.device_label || 'Unknown Device'}
          </p>
          {session.is_current && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-success/15 text-success">
              Current
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground">
          {session.ip_address && (
            <span className="flex items-center gap-1">
              <Globe className="w-3 h-3" />
              {session.ip_address}
            </span>
          )}
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Active {timeAgo(session.last_active_at)}
          </span>
        </div>
      </div>
    </div>

    {!session.is_current && (
      <Button
        variant="ghost"
        size="sm"
        className="text-destructive hover:text-destructive/80 hover:bg-destructive/15 flex-shrink-0"
        onClick={onRevoke}
        disabled={isRevoking}
      >
        Sign Out
      </Button>
    )}
  </div>
);

export default SessionsPage;
